import { createContext, useContext, useState, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import './demo.css';
import { appendSavedSite, deleteSavedSiteById, loadSavedSites, type SitePolygon, type SiteFormData, type SavedSite } from './demoStorage';

/* ── Toast ── */
export type ToastType = 'success' | 'error' | 'warning';
interface ToastItem { id: number; msg: string; type: ToastType; }

/* ── Right Panel ── */
interface PanelState { title: string; body: ReactNode; footer?: ReactNode; }

interface DemoCtx {
    toasts: ToastItem[];
    toast: (msg: string, type?: ToastType) => void;
    panel: PanelState | null;
    openPanel: (p: PanelState) => void;
    closePanel: () => void;
    /* polygon drawing */
    pendingPolygon: SitePolygon[] | null;
    setPendingPolygon: (pts: SitePolygon[]) => void;
    /* form <-> footer bridge */
    registerSiteFormGetter: (fn: (() => SiteFormData) | null) => void;
    savePendingSite: () => SavedSite | null;
    /* saved sites (persisted) */
    savedSites: SavedSite[];
    deleteSavedSite: (id: string) => void;
    /* cross-page drawing mode trigger */
    drawingModeRequest: boolean;
    requestDrawingMode: () => void;
    clearDrawingModeRequest: () => void;
}

const Ctx = createContext<DemoCtx>({} as DemoCtx);
export const useDemoCtx = () => useContext(Ctx);

const ICON: Record<ToastType, string> = { success: '✅', error: '❌', warning: '⚠️' };

export function DemoProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts]           = useState<ToastItem[]>([]);
    const [panel,  setPanel]            = useState<PanelState | null>(null);
    const [pendingPolygon, setPendingPolygon] = useState<SitePolygon[] | null>(null);
    const [savedSites, setSavedSites]   = useState<SavedSite[]>(() => loadSavedSites());
    const [drawingModeRequest, setDrawingModeRequest] = useState(false);
    const idRef = useRef(0);
    const siteFormGetterRef = useRef<(() => SiteFormData) | null>(null);

    const toast = useCallback((msg: string, type: ToastType = 'success') => {
        const id = ++idRef.current;
        setToasts(p => [...p, { id, msg, type }]);
        setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3000);
    }, []);

    const openPanel  = useCallback((p: PanelState) => setPanel(p), []);
    const closePanel = useCallback(() => {
        setPanel(null);
        setPendingPolygon(null);
        siteFormGetterRef.current = null;
    }, []);

    const registerSiteFormGetter = useCallback((fn: (() => SiteFormData) | null) => {
        siteFormGetterRef.current = fn;
    }, []);

    const deleteSavedSite = useCallback((id: string) => {
        deleteSavedSiteById(id);
        setSavedSites(prev => prev.filter(s => s.id !== id));
    }, []);

    const requestDrawingMode   = useCallback(() => setDrawingModeRequest(true),  []);
    const clearDrawingModeRequest = useCallback(() => setDrawingModeRequest(false), []);

    const savePendingSite = useCallback((): SavedSite | null => {
        const getter = siteFormGetterRef.current;
        if (!getter) return null;
        const form = getter();
        const polygon = pendingPolygon ?? [];
        const site = appendSavedSite(polygon, form);
        setSavedSites(prev => [...prev, site]);
        return site;
    }, [pendingPolygon]);

    return (
        <Ctx.Provider value={{
            toasts, toast,
            panel, openPanel, closePanel,
            pendingPolygon, setPendingPolygon,
            registerSiteFormGetter, savePendingSite,
            savedSites, deleteSavedSite,
            drawingModeRequest, requestDrawingMode, clearDrawingModeRequest,
        }}>
            {children}
            {/* Toast portal */}
            <div className="gmw-toast-wrap">
                {toasts.map(t => (
                    <div key={t.id} className={`gmw-toast ${t.type}`}>
                        <span style={{ fontSize: 16 }}>{ICON[t.type]}</span>
                        <span>{t.msg}</span>
                    </div>
                ))}
            </div>
            {/* Right panel portal */}
            <div className={`gmw-rpnl-overlay${panel ? ' open' : ''}`} onClick={closePanel} />
            <div className={`gmw gmw-rpanel${panel ? ' open' : ''}`}>
                {panel && (
                    <>
                        <div className="rpnl-hd">
                            <span className="rpnl-title">{panel.title}</span>
                            <button className="rpnl-close" onClick={closePanel}>✕</button>
                        </div>
                        <div className="rpnl-body">{panel.body}</div>
                        {panel.footer && <div className="rpnl-ft">{panel.footer}</div>}
                    </>
                )}
            </div>
        </Ctx.Provider>
    );
}
